import React, {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
  MouseEvent,
} from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import ClearIcon from "@mui/icons-material/Clear";
import HistoryIcon from "@mui/icons-material/History";
import "./InputBox.scss";
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_DensityState,
  type MRT_Row,
  MRT_SortingState,
  type MRT_TableOptions,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_VisibilityState,
  useMaterialReactTable,
} from "material-react-table";
import {
  Box,
  Button,
  Chip,

  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Accordion from "react-bootstrap/Accordion";
import { Col, Container, Input, Row, FormGroup, Form } from "reactstrap";
import axiosInstance from "../../Utils/axiosInstance";
import { backendbaseUrl } from "../../Utils/baseUrl";
import { toast } from "react-toastify";
import { showDeleteConfirmModal } from "../../Components/ConfirmModal";
import { CompactTextField } from "../../Components/SharedComponents/SharedComponents";
import FileUpload from "../../Components/FileUpload/FileUpload";
import BrokerTemplateDownloadButton from "../../Components/TemplateDownloadButton/BrokerTemplateDownloadButton";
import { useNavigate } from "react-router-dom";
import { showActiveConfirmModal } from "../../Components/ConfirmModal/ConfirmFactory";
import { formatDate } from "../../Helpers/DateFormat.helper";
import { CostTypeRow } from "../../Types/CostType.type";
import { TableColumnVisibilityMenu } from "../../Components/SharedComponents/TableColumnVisibilityMenu";
import { CommonToolbarInternalActions } from "../../Components/SharedComponents/CommonTableToolbarActions";

const validateRequired = (value: string) => !!value?.trim().length;


// ─── Main Component ───────────────────────────────────────────────────────────
const Example = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isFirstRender = useRef(true);
  const [costType, setCostType] = useState("");
  const [description, setDescription] = useState("");
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [inputBackgroundColour, setInputBackgroundColour] = useState("");
  const [filterModes, setFilterModes] = useState<{ mode: string; id: string; value: unknown }[]>([]);
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({});
  const [density, setDensity] = useState<MRT_DensityState>("comfortable");
  const [globalFilter, setGlobalFilter] = useState<string | undefined>(undefined);
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [showColumnFilters, setShowColumnFilters] = useState(true);
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });
  const [totalCount, setTotalCount] = useState(0);
  const handleFileUploadOpen = () => setOpenFileUpload(true);
  const [openFileUpload, setOpenFileUpload] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [isFormModified, setIsFormModified] = useState(false);

  // ── Persist table state to localStorage ──────────────────────────────────
  const LS_PREFIX = "mrt_cost_type_table";

  useEffect(() => {
    const load = (key: string) => {
      try { return JSON.parse(localStorage.getItem(`${LS_PREFIX}_${key}`) || "null"); } catch { return null; }
    };
    const cf = load("columnFilters"); if (cf) setColumnFilters(cf);
    const cv = load("columnVisibility"); if (cv) setColumnVisibility(cv);
    const d = load("density"); if (d) setDensity(d);
    const gf = load("globalFilter"); if (gf !== null) setGlobalFilter(gf || undefined);
    const sgf = load("showGlobalFilter"); if (sgf !== null) setShowGlobalFilter(sgf);
    const scf = load("showColumnFilters"); if (scf !== null) setShowColumnFilters(scf);
    const s = load("sorting"); if (s) setSorting(s);
    isFirstRender.current = false;
  }, []);

  useEffect(() => { if (!isFirstRender.current) localStorage.setItem(`${LS_PREFIX}_columnFilters`, JSON.stringify(columnFilters)); }, [columnFilters]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem(`${LS_PREFIX}_columnVisibility`, JSON.stringify(columnVisibility)); }, [columnVisibility]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem(`${LS_PREFIX}_density`, JSON.stringify(density)); }, [density]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem(`${LS_PREFIX}_globalFilter`, JSON.stringify(globalFilter ?? "")); }, [globalFilter]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem(`${LS_PREFIX}_showGlobalFilter`, JSON.stringify(showGlobalFilter)); }, [showGlobalFilter]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem(`${LS_PREFIX}_showColumnFilters`, JSON.stringify(showColumnFilters)); }, [showColumnFilters]);
  useEffect(() => { if (!isFirstRender.current) localStorage.setItem(`${LS_PREFIX}_sorting`, JSON.stringify(sorting)); }, [sorting]);

  useEffect(() => {
    if (!isFirstRender.current) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [sorting]);

  useEffect(() => {
    if (!isFirstRender.current) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [columnFilters]);

  // ── Hooks ─────────────────────────────────────────────────────────────────
  const { mutateAsync: createUser, isPending: isCreatingUser } = useCreateUser();
  const { data, isError: isLoadingUsersError, isFetching: isFetchingUsers, isLoading: isLoadingUsers } =
    useGetData(filterModes, pagination, setTotalCount, sorting);
  const { mutateAsync: updateUser, isPending: isUpdatingUser } = useUpdateUser();
  const { mutateAsync: deleteUser, isPending: isDeletingUser } = useDeleteUser();
  const { mutateAsync: toggleActive } = useToggleActive();

  // ── Navigation ────────────────────────────────────────────────────────────
  const handlehistory = () => navigate("/settings/log/history/cost-type");
  const openrowhistory = (row: any) => navigate(`/settings/log/history/cost-type/${row.id}`);

  // ── File Upload ───────────────────────────────────────────────────────────
  const handleFileUploadSubmit = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axiosInstance.post(
        `${backendbaseUrl}/paper-app/cost-types-upload-file/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(response.data?.message || "File uploaded and data added successfully!");


      return {
        success: true,
        message: response.data?.message,
        data: response.data,
      };
    } catch (error: any) {
      const data = error.response?.data;

      let msg = "Failed to upload file";
      if (data?.message) {
        msg = data.message;
      }

      toast.error(msg);

      return {
        success: false,
        message: msg,
      };
    }
  };

  // ── Create ────────────────────────────────────────────────────────────────
  const createCostType = async (payload: { cost_name: string; description?: string }) => {
    if (!payload.cost_name) {
      setValidationErrors((prev) => ({ ...prev, cost_name: "Cost Type is Required" }));
      toast.error("Cost Type is required!");
      return;
    }
    try {
      await axiosInstance.post(`${backendbaseUrl}/paper-app/cost-types/`, payload);
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Cost Type added successfully!");
    } catch (error: any) {
      let msg = "Failed to add Cost Type. Please try again.";
      if (error.response?.data) {
        const d = error.response.data;
        if (typeof d === "object" && !Array.isArray(d)) {
          const messages = Object.values(d).flat().filter((m) => typeof m === "string");
          if (messages.length) msg = (messages as string[]).join("\n");
        }
        if (typeof d === "string") msg = d;
      }
      toast.error(msg);
    }
  };

  const formCreate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors: Record<string, string | undefined> = {};
    if (!costType.trim()) errors.cost_name = "Cost Type is required";
    else if (costType.trim().length > 250) errors.cost_name = "Cost Type name cannot exceed 250 characters";
    if (Object.keys(errors).length) { setValidationErrors(errors); return; }
    setValidationErrors({});
    await createCostType({ cost_name: costType.trim(), description: description.trim() });
    setCostType("");
    setDescription("");
  };

  // ── Inline Create (table row) ──────────────────────────────────────────────
  const handleCreateUser: MRT_TableOptions<CostTypeRow>["onCreatingRowSave"] = async ({ values, table }) => {
    if (!values.cost_name?.trim()) {
      setValidationErrors({ cost_name: "Cost Type is Required" });
      return;
    }
    setValidationErrors({});
    await createCostType({ cost_name: values.cost_name.trim(), description: values.description?.trim() });
    table.setCreatingRow(null);
  };

  // ── Update ────────────────────────────────────────────────────────────────
  const handleeditsave: MRT_TableOptions<CostTypeRow>["onEditingRowSave"] = async ({ values, table, row }) => {
    const updatedUser = { ...row.original, ...values };
    if (!updatedUser.cost_name?.trim()) {
      setValidationErrors({ cost_name: "Cost Type is Required" });
      toast.error("Cost Type is Required");
      return;
    }
    setValidationErrors({});
    try {
      await updateUser(updatedUser);
      toast.success("Cost Type updated successfully!");
      table.setEditingRow(null);
    } catch (error: any) {
      toast.error("Failed to update Cost Type. Please try again.");
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const openDeleteConfirmModal = (row: MRT_Row<CostTypeRow>) => {
    showDeleteConfirmModal({
      title: "Are you sure you want to delete this cost type?",
      content: `This will permanently delete "${row.original.cost_name}".`,
      onOk: () => {
        deleteUser(row.original.id)
            .then((data: any) => toast.success(data?.message || "Cost Type deleted successfully!"))
          .catch((error: any) => {
            // ← Extract backend message
            const backendMsg = error?.response?.data?.message;
            const usedIds = error?.response?.data?.used_ids;

            if (backendMsg) {
              toast.error(backendMsg);
            } else {
              toast.error("Failed to delete Cost Type. Please try again.");
            }
          });
      },
    });
  };

  // ── Toggle Active ─────────────────────────────────────────────────────────────
  const handleToggleActive = async (row: MRT_Row<CostTypeRow>) => {
    const current = row.original.is_active;
    const action = current ? "deactivate" : "activate";

    try {
      await toggleActive({
        id: row.original.id,
        is_active: !current,
        cost_name: row.original.cost_name,
        description: row.original.description,
      });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success(`Cost Type ${!current ? "activated" : "deactivated"} successfully!`);
    } catch {
      toast.error("Failed to update status. Please try again.");
    }
  };
  const handleCopyRow = (row: any) => {
    if (!row) return;
    const rowData = row.original;
    const textToCopy = JSON.stringify(rowData, null, 2);
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(textToCopy).catch(console.error);
    }
    let jsonData: any;
    try {
      jsonData = JSON.parse(textToCopy);
      setCostType(jsonData.cost_name || "");
      setDescription(jsonData.description || "");
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Copied and pasted successfully");
      setInputBackgroundColour("#83bf6e26");
      setTimeout(() => setInputBackgroundColour(""), 2400);
    } catch {
      setCostType(textToCopy);
    }
  };

  const handleExportRows = async (rows: MRT_Row<any>[]) => {
    if (!rows.length) { toast.warn("Please select at least one row to export"); return; }
    try {
      const selectedData = rows.map((r) => r.original);
      const headers = ["id", "cost_name", "description", "is_active", "created_at", "created_user"];
      const csv = [
        headers.join(","),
        ...selectedData.map((d: any) =>
          headers.map((h) => `"${String(d[h] ?? "").replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "selected_cost_types.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Export started successfully");
    } catch { toast.error("Failed to export selected rows"); }
  };

  const handleDataDownload = async () => {
    try {
      const response = await axiosInstance.get(
        `${backendbaseUrl}/paper-app/cost-types-export/`,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      if (!response.data) {
        throw new Error("No data received from server");
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "all_cost_types.csv");
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success("All Data Downloaded Successfully");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download all data");
    }
  };
  const handleBulkDelete = async (rows: MRT_Row<any>[]) => {
    const rowIds = rows.map((r) => r.original.id);
    if (!rowIds.length) { toast.warn("Please select at least one cost type to delete."); return; }
    showDeleteConfirmModal({
      title: `Delete ${rowIds.length} selected cost type(s)?`,
      content: `This will permanently delete ${rowIds.length} cost type(s). Continue?`,
      onOk: async () => {
        const loadingToast = toast.loading(`Deleting ${rowIds.length} cost type(s)...`);
        try {
          const payload = { ids: rowIds.filter(Boolean) };
          const response = await axiosInstance.delete(
            `${backendbaseUrl}/paper-app/cost-types/Bulk-Delete/`,
            { data: payload, headers: { "Content-Type": "application/json" } }
          );

          const { deleted_count, deleted_ids, message } = response.data || {};

          if (Array.isArray(message)) {
            const deletedNum = deleted_count ?? deleted_ids?.length ?? 0;
            if (deletedNum > 0) {
              toast.success(`${deletedNum} cost type(s) deleted successfully`);
            }
            toast.error(message.join(" "));
          } else if (deleted_count > 0 || (deleted_ids && deleted_ids.length > 0)) {
            toast.success(message || `Successfully deleted ${rowIds.length} cost type(s)`);
          } else {
            toast.error(message || "Failed to delete Cost Type. Please try again.");
          }

          queryClient.invalidateQueries({ queryKey: ["users"] });
          table.setRowSelection({});
        } catch (error: any) {
          const backendMsg = error?.response?.data?.message;
          if (Array.isArray(backendMsg)) {
            toast.error(backendMsg.join(" "));
          } else if (backendMsg) {
            toast.error(backendMsg);
          } else {
            toast.error("Failed to delete Cost Type. Please try again.");
          }
        } finally {
          toast.dismiss(loadingToast);
        }
      },
    });
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = useMemo<MRT_ColumnDef<CostTypeRow>[]>(
    () => [
      {
        accessorKey: "cost_name",
        header: "Cost Type Name",
        enableEditing: true,
        size: 220,
        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => table.setEditingRow(row),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            "&:hover": { backgroundColor: "rgba(44, 4, 4, 0.05)" },
            ...(table.getState().editingRow?.id === row.id && {
              backgroundColor: "rgba(0, 128, 0, 0.1)",
            }),
          },
        }),
        muiEditTextFieldProps: {
          required: true,
          // onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
          //   if (/^[0-9]$/.test(e.key)) e.preventDefault();
          // },
          onFocus: () => setValidationErrors((prev) => ({ ...prev, cost_name: undefined })),
          error: !!validationErrors?.cost_name,
          helperText: validationErrors?.cost_name,
        },
      },
      {
        accessorKey: "description",
        header: "Description",
        enableEditing: true,
        size: 220,
        muiTableBodyCellProps: ({ row, table }) => ({
          onDoubleClick: () => table.setEditingRow(row),
          sx: {
            cursor: "pointer",
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: "rgba(44, 4, 4, 0.05)" },
          },
        }),
        Cell: ({ cell }) => (
          <span style={{ fontSize: "0.78rem" }}>
            {cell.getValue<string>() || "—"}
          </span>
        ),
      },
      {
        id: "is_active",
        accessorFn: (row) => (row.is_active ? "Yes" : "No"),
        header: "Active",
        enableEditing: false,
        size: 220,
        Cell: ({ cell }) => (
          <span style={{
            color: cell.getValue<string>() === "Yes" ? "#2e7d32" : "#c62828",
            fontWeight: 600
          }}>
            {cell.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "created_at_over_ridden",
        header: "Updated At",
        enableEditing: false,
        size: 220,
        Cell: ({ cell }) => (
          <span style={{ fontSize: "0.78rem", color: "#555", whiteSpace: "nowrap" }}>
            {formatDate(cell.getValue<string>())}
          </span>
        ),
      },
      {
        accessorKey: "created_user_name",
        header: "Updated By",
        enableEditing: false,
        size: 220,
        Cell: ({ cell }) => (
          <span style={{ fontSize: "0.78rem", color: "#555", whiteSpace: "nowrap" }}>
            {formatDate(cell.getValue<string>())}
          </span>
        ),
      },
    ],
    [validationErrors]
  );

  // ── Table ─────────────────────────────────────────────────────────────────
  const table = useMaterialReactTable<CostTypeRow>({
    columns,
    data: data?.results || [],
    enableStickyHeader: false,
    enableStickyFooter: true,
    enableClickToCopy: true,
    enableKeyboardShortcuts: true,
    enableColumnActions: true,
    enableColumnDragging: true,
    enableGlobalFilter: false,
    enableGrouping: true,
    enableHiding: true,
    enableSorting: true,
    enableColumnPinning: true,
    enableRowSelection: true,
    enableColumnResizing: true,
    enableFilterMatchHighlighting: true,
    enableDensityToggle: false,
    enableColumnOrdering: true,
    enableRowOrdering: false,
    enableEditing: true,
    manualSorting: false,
    manualPagination: true,
    manualFiltering: false,
    rowCount: totalCount,
    createDisplayMode: "row",
    editDisplayMode: "row",
    getRowId: (row) => row.id,
    enableColumnFilterModes: true,
    initialState: { showColumnFilters: true },


    onPaginationChange: setPagination,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onDensityChange: setDensity,
    onGlobalFilterChange: setGlobalFilter,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onSortingChange: setSorting,

    state: {
      columnVisibility,
      sorting,
      columnFilters,
      pagination,
      isLoading: isLoadingUsers,
      isSaving: isCreatingUser || isUpdatingUser || isDeletingUser,
      showAlertBanner: isLoadingUsersError,
      showProgressBars: isFetchingUsers,
      showColumnFilters,
    },

    muiToolbarAlertBannerProps: isLoadingUsersError
      ? { color: "error", children: "Error loading data" }
      : undefined,

    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateUser,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleeditsave,

    muiTopToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
        "& .MuiBox-root.css-wsew38": {
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        },
      },
      className: "top-toolbar-wrapper__new",
    }),
    muiTableContainerProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-container-wrapper__new",
    }),
    muiTableFooterProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
      },
      className: "table-footer-wrapper__new",
    }),
    muiBottomToolbarProps: ({ table }) => ({
      sx: {
        backgroundColor: "#fff",
        padding: "8px",
      },
      className: "bottom-toolbar-wrapper__new",
    }),
    muiTablePaperProps: { elevation: 0, sx: { borderRadius: "0", border: "1" } },
    muiTableHeadCellProps: {
      sx: { border: "1px solid #e0e0e087", fontStyle: "normal", fontWeight: "normal" },
    },

    muiTableBodyRowProps: ({ row, table }) => ({
      onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
        if (event.key === "Enter") {
          event.preventDefault();
          const editingRow = table.getState().editingRow;
          if (editingRow) {
            const saveHandler = table.options.onEditingRowSave;
            if (saveHandler) {
              saveHandler({
                exitEditingMode: () => table.setEditingRow(null),
                row: editingRow,
                table,
                values: { ...editingRow.original, ...editingRow._valuesCache },
              });
            }
          }
        }
      },
    }),

    displayColumnDefOptions: {
      "mrt-row-actions": { enableColumnActions: true, size: 180 },
      "mrt-row-select": { enableColumnActions: true, enableHiding: true },
    },

    renderToolbarInternalActions: (props) => <CommonToolbarInternalActions {...props} />,    

    // ── Top Toolbar Actions ────────────────────────────────────────────────
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        className="table-download__wrapper"
        sx={{
          display: "flex",
          gap: "6px",
          padding: "0px",
          flexWrap: "wrap",
        }}
      >
        <Button startIcon={<UploadFileIcon />} onClick={handleFileUploadOpen}>
          Upload File
        </Button>
        <Button
          onClick={() => handleDataDownload()}
          startIcon={<BrowserUpdatedIcon />}
        >
          Export All Data
        </Button>

        {/* <Button
          onClick={() => handlehistory()}
          startIcon={<HistoryIcon />}
        >
          History Log
        </Button> */}

        <BrokerTemplateDownloadButton
          apiEndpoint="/paper-app/cost-types-download-template/"
          buttonText="Download Template"
          fileName="cost_type_template.csv"
        />
        {/* <Button
          disabled={table.getPrePaginationRowModel().rows.length === 0}
          //export all rows, including from the next page, (still respects filtering and sorting)

          startIcon={<FileDownloadIcon />}
        >
          Export All Rows
        </Button>
        <Button
          disabled={table.getRowModel().rows.length === 0}
          //export all rows as seen on the screen (respects pagination, sorting, filtering, etc.)

          startIcon={<FileDownloadIcon />}
        >
          Export Page Rows
        </Button> */}
        <Button
          disabled={
            !table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()
          }
          //only export selected rows
          onClick={() => handleExportRows(table.getSelectedRowModel().rows as MRT_Row<any>[])}
          startIcon={<FileDownloadIcon />}
        >
          Export Selected Rows
        </Button>
        {(table.getIsSomeRowsSelected() || table.getIsAllRowsSelected()) && (
          <Button
            style={{ color: "red" }}
            onClick={() => handleBulkDelete(table.getSelectedRowModel().rows)}
            startIcon={<DeleteIcon />}
          >
            Delete Selected ({table.getSelectedRowModel().rows.length})
          </Button>
        )}
      </Box>
    ),

    // ── Row Actions ────────────────────────────────────────────────────────
    renderRowActions: ({ row, table }) => (
      <Box sx={{ display: "flex", gap: "rem", alignItems: "center" }}>
        <Tooltip title="Edit">
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy Row">
          <IconButton onClick={() => handleCopyRow(row)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="History" className="History">
          <IconButton color="error" onClick={() => openrowhistory(row)}>
            <HistoryIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={row.original.is_active ? "Deactivate" : "Activate"}>
          <span>
            <Form>
              <FormGroup switch>
                <Input
                  type="switch"
                  checked={row.original.is_active}
                  onChange={() => handleToggleActive(row)}
                />
              </FormGroup>
            </Form>
          </span>
        </Tooltip>
      </Box>
    ),
  });

  // Sync filter modes
  useEffect(() => {
    const transformed = columnFilters.map((filter) => ({
      ...filter,
      mode: table.getColumn(filter.id)?.getFilterFn()?.name || "contains",
    }));
    setFilterModes(transformed);
  }, [columnFilters, table]);

  // ── Context Menu ──────────────────────────────────────────────────────────
  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenu(contextMenu === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
  };
  const handleClose = () => setContextMenu(null);

  useEffect(() => {
    setIsFormModified(Boolean(costType.trim()) || Boolean(description.trim()));
  }, [costType, description]);

  const handleClear = (): void => {
    setCostType("");
    setDescription("");
    setValidationErrors({});
    setInputBackgroundColour("");
    setIsFormModified(false);
    toast.success("Form cleared successfully", {
      autoClose: 2000,
    });
  };  

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="page-body">
      <Container fluid>
        <Row>
          <div>
            <div className="table-ad">
              {/* ── Add Form ── */}
              <Accordion defaultActiveKey="0" className="table-ad__addInputs">
                <Accordion.Item eventKey="0">
                  <Accordion.Header className="accordion_button">
                    Add New Cost Type
                  </Accordion.Header>
                  <Accordion.Body>
                    <div className="form_contain">
                      <div
                        className={`flex formWrapper ${inputBackgroundColour !== "" ? "inputboxcustom" : ""}`}
                        style={{ backgroundColor: inputBackgroundColour }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            p: { xs: 1, sm: 1 },
                            pt: "20px !important",
                            backgroundColor: "#ffff",
                            borderRadius: "4px",
                            border: "1px solid #e0e0e0",
                          }}
                        >
                          <Box
                            component="form"
                            onSubmit={formCreate}
                            className="form_default"
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              alignItems: "flex-start",
                              justifyContent: "start",
                              gap: 0.5,
                              "& > *": {
                                minWidth: { xs: "100%", sm: "48%", md: "23%", lg: "15%", xl: "7.5%" },
                              },
                            }}
                          >
                            <CompactTextField
                              label="Enter Cost Type *"
                              value={costType}
                              type="text"
                              onChange={(value) => {
                                setCostType(value);
                              }}
                              name="costType"
                              error={Boolean(validationErrors?.cost_name)}
                              helperText={validationErrors?.cost_name || ""}
                              validateOnBlur={(value) => {
                                if (!validateRequired(value)) {
                                  setValidationErrors((prev) => ({ ...prev, cost_name: "Cost Type is Required" }));
                                } else {
                                  setValidationErrors((prev) => ({ ...prev, cost_name: undefined }));
                                }
                                return null;
                              }}
                              slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: {
                                  endAdornment: costType && (
                                    <InputAdornment position="end">
                                      <IconButton size="small" onClick={() => setCostType("")} edge="end">
                                        <ClearIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                },
                              }}
                              sx={{
                                flex: { xs: "1 1 auto", sm: "1 1 70%" },
                                "& .MuiInputBase-root": { fontSize: "0.7rem", height: { xs: "36px", sm: "32px" } },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.7rem",
                                  transform: "translate(14px, 8px) scale(1)",
                                  "&.MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
                                },
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#C0C0C0" },
                                maxWidth: { sm: "250px", xs: "100%" },
                              }}
                            />

                            {/* Description Field */}
                            <CompactTextField
                              label="Description"
                              value={description}
                              type="text"
                              onChange={(value) => setDescription(value)}
                              name="description"
                              error={false}
                              helperText=""
                              slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: {
                                  endAdornment: description && (
                                    <InputAdornment position="end">
                                      <IconButton size="small" onClick={() => setDescription("")} edge="end">
                                        <ClearIcon fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                },
                              }}
                              sx={{
                                flex: { xs: "1 1 auto", sm: "1 1 70%" },
                                "& .MuiInputBase-root": { fontSize: "0.7rem", height: { xs: "36px", sm: "32px" } },
                                "& .MuiInputLabel-root": {
                                  fontSize: "0.7rem",
                                  transform: "translate(14px, 8px) scale(1)",
                                  "&.MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
                                },
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#C0C0C0" },
                                maxWidth: { sm: "250px", xs: "100%" },
                              }}
                            />

                            <Button
                              variant="contained"
                              type="submit"
                              disabled={!costType.trim()}
                              sx={{
                                height: { xs: "36px", sm: "32px" },
                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                minWidth: { xs: "100%", sm: "100px" },
                                maxWidth: { sm: "150px" },
                                backgroundColor: "#00bcd4",
                                textTransform: "none",
                                fontSize: "0.75rem",
                                "&:hover": { backgroundColor: "#008c9e" },
                              }}
                            >
                              Add
                            </Button>
                            <Button
                              onClick={handleClear}
                              variant="outlined"
                              fullWidth
                              disabled={!isFormModified}
                              sx={{
                                height: { xs: "36px", sm: "32px" },
                                flex: { xs: "1 1 auto", sm: "0 0 auto" },
                                minWidth: { xs: "100%", sm: "100px" },
                                maxWidth: { sm: "150px" },
                                marginLeft: { sm: "8px" },
                                textTransform: "none",
                                fontSize: "0.75rem",
                                borderColor: "#e0e0e0",
                                color: "#222",
                                backgroundColor: "#e6f3f5",
                                transition: "all 0.3s ease-in-out",
                                "&:hover": {
                                  borderColor: "#008c9e",
                                  backgroundColor: "#d1e8eb",
                                  transform: "scale(1.02)",
                                },
                                "&.Mui-disabled": {
                                  backgroundColor: "#f5f5f5",
                                  color: "#999",
                                  borderColor: "#e0e0e0",
                                },
                              }}
                            >
                              Clear
                            </Button>
                            
                          </Box>
                        </Box>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>

              {/* ── Table ── */}
              <div className="table-ad__wrapper" onContextMenu={handleContextMenu}>
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable table={table} />
                  <Menu
                    open={contextMenu !== null}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                  >
                    <MenuItem onClick={handleClose}>Close</MenuItem>
                  </Menu>
                </div>
                <FileUpload
                  open={openFileUpload}
                  onFileUploadClose={() => setOpenFileUpload(false)}
                  onFileUploadSubmit={handleFileUploadSubmit}
                  acceptedFileTypes=".csv"
                />
              </div>
            </div>
          </div>
        </Row>
      </Container>
    </div>
  );
};

function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (user: any) => {
      await new Promise((resolve) => setTimeout(resolve, 300));
      return Promise.resolve();
    },
    onMutate: (newUserInfo: any) => {
      queryClient.setQueryData(["users"], (prevUsers: any) =>
        [...(prevUsers || []), { ...newUserInfo, id: Math.random().toString(36).substring(7) }]
      );
    },
  });
}

function useGetData(
  filterModes: { mode: string; id: string; value: unknown }[],
  pagination: { pageIndex: number; pageSize: number },
  setTotalCount: (count: number) => void,
  sorting: any
) {
  return useQuery<{ results: CostTypeRow[]; totalCount: number }>({
    queryKey: ["users", filterModes, pagination, sorting],
    queryFn: async () => {
      let url = `${backendbaseUrl}/paper-app/cost-types/?page=${pagination.pageIndex + 1}&page_size=${pagination.pageSize}`;

      if (filterModes.length > 0) {
        console.log("filterModes---,", filterModes);
        const queryParams = filterModes
          .map(
            (e) =>
              `fieldName__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${e.id
              }=${String(e.value)}__d39d26f3-8442-44d0-8a7f-66cfcbb2c781__${e.mode
              }`
          )
          .join("&");

        url += `&${queryParams}`;
      }
      console.log("sortparams:", sorting);
      if (sorting.length > 0) {
        url += `&sortField_${sorting[0].id}=${String(sorting[0].desc)}_sorting`;

        console.log("yesss");
      } else {
        console.log("sortParams");
      }

      const response = await axiosInstance.get(url);
      const results = Array.isArray(response.data) ? response.data : response.data.results ?? [];
      const total = Array.isArray(response.data) ? response.data.length : response.data.count ?? 0;
      setTotalCount(total);
      return { results: results as CostTypeRow[], totalCount: total };
    },
    refetchOnWindowFocus: false,
  });
}

function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (user: CostTypeRow) => {
      if (!user?.id) throw new Error("User object is invalid or missing ID");
      const { data } = await axiosInstance.put(
        `${backendbaseUrl}/paper-app/cost-types/update/${user.id}`,
        { cost_name: user.cost_name, description: user.description }
      );
      return data;
    },
    onMutate: async (newUserInfo: CostTypeRow) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });
      const previousUsers = queryClient.getQueryData(["users"]);
      queryClient.setQueryData(["users"], (oldData: any) =>
        oldData?.map((u: CostTypeRow) => (u.id === newUserInfo.id ? newUserInfo : u))
      );
      return { previousUsers };
    },
    onError: (err, newUser, context) => {
      queryClient.setQueryData(["users"], context?.previousUsers);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await axiosInstance.delete(
        `${backendbaseUrl}/paper-app/cost-types/Bulk-Delete/`,
        {
          data: { ids: [userId] },
          headers: { "Content-Type": "application/json" },
        }
      );
      return data;
    },
    onMutate: async (userId: string) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });
      const previousUsers = queryClient.getQueryData(["users"]);
      queryClient.setQueryData(["users"], (prevUsers: any) => {
        if (!prevUsers) return prevUsers;
        if (prevUsers.results) {
          return { ...prevUsers, results: prevUsers.results.filter((u: CostTypeRow) => u.id !== userId) };
        }
        return prevUsers.filter((u: CostTypeRow) => u.id !== userId);
      });
      return { previousUsers };
    },
    onError: (err, userId, context) => {
      queryClient.setQueryData(["users"], context?.previousUsers);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}

function useToggleActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_active, cost_name, description }: {
      id: string;
      is_active: boolean;
      cost_name: string;
      description?: string;
    }) => {
      const { data } = await axiosInstance.put(
        `${backendbaseUrl}/paper-app/cost-types/update/${id}`,
        { cost_name, description, is_active }
      );
      return data;
    },
    onMutate: async ({ id, is_active }) => {
      await queryClient.cancelQueries({ queryKey: ["users"] });
      const previousData = queryClient.getQueryData(["users"]);
      queryClient.setQueryData(["users"], (old: any) => {
        if (!old) return old;
        if (old.results) {
          return { ...old, results: old.results.map((u: CostTypeRow) => u.id === id ? { ...u, is_active } : u) };
        }
        return old.map((u: CostTypeRow) => u.id === id ? { ...u, is_active } : u);
      });
      return { previousData };
    },
    onError: (err, vars, context) => {
      queryClient.setQueryData(["users"], context?.previousData);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["users"] }),
  });
}
const queryClient = new QueryClient();

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Example />
    </QueryClientProvider>
  );
}